import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { apiClient } from '../services/api'
import { OtpInput } from '../components/auth/OtpInput'
import { CountdownTimer } from '../components/auth/CountdownTimer'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'

// Registration key disabled for now — can be re-enabled by uncommenting
// const REGISTRATION_KEY = '01600'

function createRegisterFormSchema(t: TFunction) {
  return z.object({
    email: z.string().email(t('validation.invalidEmail')),
    phone: z.string().min(7, t('validation.phoneTooShort')),
    displayName: z.string().min(2, t('validation.nameTooShort')),
    apartmentNumber: z.string().min(1, t('validation.apartmentRequired')),
    parkingSpaceNumber: z.string().optional(),
    // registrationKey: z.string().min(1, t('validation.registrationKeyRequired')),
    termsAccepted: z.literal(true, { errorMap: () => ({ message: t('validation.termsRequired') }) }),
  })
}

type RegisterForm = z.infer<ReturnType<typeof createRegisterFormSchema>>

const TERMS_URL = '/lataus_kayttoehdot.pdf'

type Step = 'form' | 'otp' | 'success'

export function RegisterPage() {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('form')
  const [formData, setFormData] = useState<RegisterForm | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [otpExpiry, setOtpExpiry] = useState(0)
  const [sending, setSending] = useState(false)
  const [otpValue, setOtpValue] = useState('')

  const { register, handleSubmit, formState: { errors, isSubmitting, isValid }, watch } =
    useForm<RegisterForm>({
      resolver: zodResolver(createRegisterFormSchema(t)),
      mode: 'onChange',
    })

  const termsChecked = watch('termsAccepted')

  async function onFormSubmit(data: RegisterForm) {
    setServerError(null)

    // Registration key validation disabled for now
    // if (data.registrationKey !== REGISTRATION_KEY) {
    //   setServerError(t('validation.invalidRegistrationKey'))
    //   return
    // }

    setSending(true)
    try {
      // Send OTP to the email
      const res = await apiClient.post<{ expiresIn: number }>('/api/auth/request-otp', {
        identifier: data.email.toLowerCase().trim(),
        channel: 'email',
        purpose: 'register',
      })
      setFormData(data)
      setOtpExpiry(res.expiresIn)
      setStep('otp')
    } catch (err: unknown) {
      const status = (err as { status?: number }).status
      if (status === 429) {
        setServerError(t('errors.tooManyRequests'))
      } else {
        setServerError(t('errors.otpSendFailed'))
      }
    } finally {
      setSending(false)
    }
  }

  async function onOtpComplete(code: string) {
    if (!formData) return
    setServerError(null)
    try {
      await apiClient.post('/api/user/register', {
        ...formData,
        otpCode: code,
      })
      setStep('success')
    } catch (err: unknown) {
      const body = (err as { body?: { message?: string } }).body
      const status = (err as { status?: number }).status
      if (status === 401) {
        setServerError(t('errors.wrongVerificationCode'))
      } else if (status === 409) {
        setServerError(t('errors.emailAlreadyRegistered'))
      } else {
        setServerError(body?.message ?? t('errors.registrationFailed'))
      }
    }
  }

  if (step === 'success') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center space-y-4 bg-white rounded-2xl shadow-md p-8">
          <div className="text-4xl text-green-600">&#10003;</div>
          <h2 className="text-xl font-bold text-brand-700">{t('register.successTitle')}</h2>
          <p className="text-brand-600 text-sm">
            {t('register.successMessage')}
          </p>
          <a href="/login" className="block text-brand-500 hover:underline text-sm">
            {t('register.backToLogin')}
          </a>
        </div>
      </div>
    )
  }

  if (step === 'otp') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-md p-8 text-center space-y-5">
          <h2 className="text-xl font-bold text-brand-700">{t('register.verifyTitle')}</h2>
          <p className="text-brand-500 text-sm">
            {t('register.otpSent')} <strong>{formData?.email}</strong>
          </p>

          <OtpInput value={otpValue} onChange={(val) => {
            setOtpValue(val)
            if (val.length === 6) onOtpComplete(val)
          }} />

          <CountdownTimer seconds={otpExpiry} onExpired={() => {
            setServerError(t('errors.expiredReturn'))
          }} />

          {serverError && <p className="text-red-500 text-sm">{serverError}</p>}

          <button onClick={() => { setStep('form'); setServerError(null) }}
            className="text-brand-500 hover:underline text-sm">
            {t('register.backToForm')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-md p-8">
        <h1 className="text-xl font-bold text-brand-700 mb-6">{t('register.title')}</h1>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          <Field label={t('register.name')} error={errors.displayName?.message}>
            <input {...register('displayName')} autoComplete="name"
              className="w-full rounded-lg border-brand-100 focus:border-brand-500 focus:ring-brand-500" />
          </Field>

          <Field label={t('register.email')} error={errors.email?.message}>
            <input {...register('email')} type="email" autoComplete="email"
              className="w-full rounded-lg border-brand-100 focus:border-brand-500 focus:ring-brand-500" />
          </Field>

          <Field label={t('register.phone')} error={errors.phone?.message}>
            <input {...register('phone')} type="tel" autoComplete="tel"
              className="w-full rounded-lg border-brand-100 focus:border-brand-500 focus:ring-brand-500" />
          </Field>

          <Field label={t('register.apartment')} error={errors.apartmentNumber?.message}>
            <input {...register('apartmentNumber')}
              className="w-full rounded-lg border-brand-100 focus:border-brand-500 focus:ring-brand-500" />
          </Field>

          <Field label={t('register.parkingSpace')}>
            <input {...register('parkingSpaceNumber')}
              placeholder={t('register.parkingSpacePlaceholder')}
              className="w-full rounded-lg border-brand-100 focus:border-brand-500 focus:ring-brand-500" />
          </Field>

          {/* Registration key field disabled for now
          <Field label={t('register.registrationKey')} error={errors.registrationKey?.message}>
            <input {...register('registrationKey')} autoComplete="off"
              placeholder={t('register.registrationKeyPlaceholder')}
              className="w-full rounded-lg border-brand-100 focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          */}

          <div className="flex items-start gap-3 pt-2">
            <input {...register('termsAccepted')} type="checkbox" id="terms"
              className="mt-0.5 h-4 w-4 rounded border-brand-100 text-brand-500 focus:ring-brand-500 cursor-pointer" />
            <label htmlFor="terms" className="text-sm text-brand-700 cursor-pointer">
              {t('register.termsAccept')}{' '}
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer"
                className="text-brand-500 hover:underline font-medium">
                {t('register.termsLink')}
              </a>
            </label>
          </div>
          {errors.termsAccepted && (
            <p className="text-red-500 text-xs">{errors.termsAccepted.message}</p>
          )}

          {serverError && <p className="text-red-500 text-sm">{serverError}</p>}

          <button
            type="submit"
            disabled={isSubmitting || sending || !isValid || !termsChecked}
            className="w-full bg-brand-500 hover:bg-brand-600 text-white font-medium py-2.5 rounded-lg
              disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-2"
          >
            {isSubmitting || sending ? t('register.submitting') : t('register.submit')}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-brand-500">
          {t('register.hasAccount')}{' '}
          <a href="/login" className="text-brand-600 hover:underline font-medium">{t('register.login')}</a>
        </p>
      </div>
    </div>
  )
}

function Field({ label, error, children }: {
  label: string; error?: string; children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-brand-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  )
}
